import React from 'react';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import AllAisleTestRunner from '../business/all-aisle-test-runner';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import TestDurationPicker, {DEFAULT_TEST_DURATION_IN_MS} from './test-duration-picker';
import humanizeDuration from 'humanize-duration';

const REFRESH_INTERVAL_IN_MS = 1 * 60 * 1000; // 1 min

const DEFAULT_PATTERN_ID = 'red';
const DEFAULT_AISLE_COUNT = 48;

const DEFAULT_STATE = {
    controllerIp: null,
    isBroadcast: true,
    patternId: DEFAULT_PATTERN_ID,
    isRunning: false,
    aisleCount: DEFAULT_AISLE_COUNT,
    testDuration: DEFAULT_TEST_DURATION_IN_MS,
    timeNow: Date.now()
};

class AllAisleTester extends React.Component {
    constructor(props) {
        super(props);
        this.runner = null;
        this.state = DEFAULT_STATE;
    }

    componentDidMount() {
        this.intervalTimer = setInterval(this.updateTimeNow, REFRESH_INTERVAL_IN_MS);
    }

    componentWillUnmount() {
        clearInterval(this.intervalTimer);
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.siteId !== this.props.siteId) {
            this.clearRunner();
            this.setState(DEFAULT_STATE);
        }
    }

    updateTimeNow = () => {
        // To trigger re-render of the buttons to show the updated remaining time of running tests
        if (this.state.isRunning) {
            this.setState({timeNow: Date.now()});
        }
    }

    getPortIdsByControllerIp() {
        const portIdsByControllerIp = {};
        // generate from port count and controllerIp
        const ip = this.state.controllerIp || '';
        portIdsByControllerIp[ip] = [];
        for (let i = 0; i < DEFAULT_AISLE_COUNT; i += 1) {
            portIdsByControllerIp[ip].push(i);
        }
        return portIdsByControllerIp;
    }

    isReady() {
        return this.state.isBroadcast || this.state.controllerIp != null;
    }

    createRunner() {
        return new AllAisleTestRunner({
            siteId: this.props.siteId,
            portIdsByControllerIp: this.getPortIdsByControllerIp(),
            callbackOnStart: () => this.setRunningStatus(true),
            callbackOnStop: () => this.setRunningStatus(false)
        });
    }

    setRunningStatus(isRunning) {
        this.setState({isRunning});
    }

    clearRunner() {
        if (this.runner != null) {
            this.runner.stop();
        }
        this.runner = null;
    }

    handleStart = async() => {
        const {patternId, testDuration} = this.state;
        this.runner = this.createRunner();
        console.log('startRunner');
        this.runner.start(patternId, testDuration);
    }

    handleStopCluster = () => {
        if (this.runner == null) {
            this.runner = this.createRunner();
        }
        this.clearRunner();
    }

    handleIpChange = (event) => {
        const controllerIp = event.target.value === '' ? null : event.target.value;
        this.setState({controllerIp});
    }

    handleIsBroadcastChange = (event) => {
        const isBroadcast = event.target.checked;
        console.log("handleIsBroadcastChange braodcast=" + isBroadcast);
        if (isBroadcast) {
            this.setState({
                controllerIp: null,
                clusterLabel: null,
            });
        }
        this.setState({isBroadcast});
    }

    handlePatternChange = (event) => {
        const patternId = event.target.value;
        this.setState({patternId});
    }

    handleTestDurationChange = (testDuration) => {
        this.setState({testDuration});
    }

    generateOnButtonLabel() {
        const {isRunning, testDuration} = this.state;
        let buttonLabel = 'ON';

        if (isRunning) {
            const testEndTime = this.runner.runEndtime;

            // testEndTime is not set until all aisles are started, which can take a few seconds
            // fallback to display the user picked testDuration if testEndTime is not calculated yet
            const remainingTime = testEndTime ? testEndTime - Date.now() : testDuration;

            const humanizedRemainingTime = humanizeDuration(remainingTime, {
                units: ['d', 'h', 'm'],
                round: true
            });

            buttonLabel += ` (${humanizedRemainingTime} remaining)`;
        }

        return buttonLabel;
    }

    renderConfigSection() {
        return (
            <Row>
                <Col md={{span: 2, offset: 4}}>
                    <Form.Group>
                        <Form.Label>Pattern</Form.Label>
                        <Form.Control as='select'
                            value={this.state.patternId}
                            disabled={this.state.isRunning}
                            onChange={this.handlePatternChange}
                        >
                            <option>red</option>
                            <option>green</option>
                            <option>blue</option>
                            <option>grey</option>
                            <option>disco</option>
                        </Form.Control>
                    </Form.Group>
                </Col>
                <Col md={{span: 2}}>
                    <TestDurationPicker
                        testDuration={this.state.testDuration}
                        onChange={this.handleTestDurationChange}
                        disabled={this.state.isRunning}
                    />
                </Col>
            </Row>
        );
    }

    renderActionButtons() {
        return (
            <Col>
                <Button className="mr-3"
                    size='lg'
                    variant={this.state.isRunning ? 'success' : 'outline-success'}
                    disabled={this.state.isRunning || !this.isReady()}
                    onClick={this.handleStart}>{this.generateOnButtonLabel()}</Button>
                <Button variant='outline-danger'
                    size='lg'
                    disabled={!this.isReady()}
                    onClick={this.handleStopCluster}>OFF</Button>
            </Col>
        );
    }

    renderInputSection() {
        return (
            <Col>
                <Form.Group as={Form.Col}>
                    <Form.Label>Controller IP address</Form.Label>
                    <Form.Control
                        disabled={this.state.isBroadcast || this.state.isRunning}
                        value={this.state.controllerIp || ''}
                        onChange={this.handleIpChange} />
                    <Form.Check className='mt-1'
                        type='checkbox'
                        disabled={this.state.isRunning}
                        checked={this.state.isBroadcast}
                        label='Broadcast ?'
                        onChange={this.handleIsBroadcastChange}/>
                </Form.Group>
            </Col>
        );
    }

    render() {
        return <div>
            <Container>
                <div className="mb-3" style={{marginTop: '6rem'}}>
                    {this.renderConfigSection()}
                </div>
                <Row className="mb-3">
                    {this.renderActionButtons()}
                </Row>
                <Row>
                    {this.renderInputSection()}
                </Row>
            </Container>
        </div>;
    }
}

export default AllAisleTester;



// WEBPACK FOOTER //
// ./src/components/devicemanagement/ledcontroller/bintester/component/aisle-tester-without-site-config.jsx